"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Eye,
  List,
  Settings,
  User,
} from "lucide-react";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { cn } from "utils/utils";
import { LGS } from "modules/lgs/model/lgs";
import { useLGSSettingsDialog } from "modules/lgs/stores/useLGSSettingsStore";
import { LgsLogo } from "modules/lgs/components/LgsLogo";
import Link from "next/link";
import { createCityUrl } from "modules/common/components/map/utils/formatCityName";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";

type Position = "top-left" | "top-right" | "bottom-left" | "bottom-right";

interface MapStoreListOverlayProps {
  stores: LGS[];
  cityName?: string;
  position?: Position;
  selectedStoreWotcId?: number | null;
  onStoreClick: (store: LGS) => void;
  className?: string;
  isAuthenticated?: boolean;
  coordinates?: { lat: number; lng: number };
  cityUserCount?: number;
  hideCityLink?: boolean;
}

const positionClasses: Record<Position, string> = {
  "top-left": "bottom-20 left-4 md:top-24 md:bottom-auto",
  "top-right": "top-4 right-4",
  "bottom-left": "bottom-4 left-4",
  "bottom-right": "bottom-4 right-4",
};

export function MapStoreListOverlay({
  stores,
  cityName,
  position = "top-left",
  selectedStoreWotcId,
  onStoreClick,
  className,
  isAuthenticated = false,
  coordinates,
  cityUserCount,
  hideCityLink = false,
}: MapStoreListOverlayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);
  const { setOpen } = useLGSSettingsDialog();

  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)"
  );

  // Auto-scroll to selected item
  useEffect(() => {
    if (isExpanded && selectedStoreWotcId && listRef.current) {
      const selectedElement = listRef.current.querySelector(
        `[data-store-id="${selectedStoreWotcId}"]`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: prefersReducedMotion ? "auto" : "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedStoreWotcId, isExpanded, prefersReducedMotion]);

  const storeCount = stores.length;

  // Generate city link with country/city hierarchical URL
  const cityLink = useMemo(() => {
    if (!cityName) return null;

    // cityName format: "Venice, Italy"
    const parts = cityName.split(",").map((s) => s.trim());

    if (parts.length >= 2) {
      const city = parts[0];
      const country = parts[parts.length - 1];
      return createCityUrl(country, city);
    }

    return null;
  }, [cityName]);

  // Collapsed state - small button
  if (!isExpanded) {
    return (
      <div
        className={cn(
          "rounded-box border-base-content/20 bg-base-100 absolute z-10 border shadow-md",
          positionClasses[position],
          className
        )}
      >
        <button
          onClick={() => setIsExpanded(true)}
          className="btn btn-ghost gap-2 rounded-full p-3"
          aria-label="Expand store list"
          title="Show store list"
        >
          <List size={ICON_SIZE} className="shrink-0" />
          <span className="badge badge-primary badge-sm">{storeCount}</span>
          <ChevronDown size={ICON_SIZE} className="shrink-0" />
        </button>
      </div>
    );
  }

  // Expanded state - simple list for all users
  return (
    <div
      className={cn(
        "rounded-box border-base-content/20 bg-base-100 absolute z-10 w-80 max-w-[calc(100vw-2rem)] border p-3 shadow-md md:w-[320px]",
        positionClasses[position],
        className
      )}
    >
      <div className="flex flex-col gap-2">
        {/* Header */}
        <div className="border-base-content/10 flex flex-col border-b pb-2">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-base-content/70 truncate text-xs">
                {cityName ? (
                  <>
                    Stores in{" "}
                    <span className="text-base-content font-semibold">
                      {cityName}
                    </span>
                  </>
                ) : (
                  <span className="text-base-content font-semibold">
                    Store Results
                  </span>
                )}
              </p>
            </div>
            <div className="ml-2 flex shrink-0 items-center gap-2">
              {isAuthenticated && (
                <button
                  onClick={() => setOpen(true)}
                  className="btn btn-ghost btn-xs p-1"
                  aria-label="Manage stores"
                  title="Manage your stores"
                >
                  <Settings size={ICON_SIZE} />
                </button>
              )}
              <span className="badge badge-primary badge-sm">{storeCount}</span>
              <button
                onClick={() => setIsExpanded(false)}
                className="btn btn-ghost btn-xs p-1"
                aria-label="Collapse store list"
                title="Hide store list"
              >
                <ChevronUp size={ICON_SIZE} />
              </button>
            </div>
          </div>
          {cityUserCount !== undefined && cityUserCount > 0 && (
            <div className="text-base-content/60 mt-1 flex items-center gap-1.5 text-xs">
              <User size={14} className="shrink-0" />
              <span>
                <strong className="text-base-content">{cityUserCount}</strong>{" "}
                active {cityUserCount === 1 ? "trader" : "traders"} in{" "}
                {cityName || "area"}
              </span>
            </div>
          )}
        </div>

        {/* View All Trades Button */}
        {cityLink && storeCount > 0 && !hideCityLink && (
          <Link
            href={`${cityLink}/haves`}
            className="btn btn-primary btn-sm w-full gap-2 overflow-hidden"
          >
            <Eye size={ICON_SIZE} className="shrink-0" />
            <span className="min-w-0 flex-1 truncate">
              View All Trades in {cityName || "Area"}
            </span>
          </Link>
        )}

        {/* Store list */}
        <div
          ref={listRef}
          className={cn(
            "max-h-[300px] overflow-y-auto md:max-h-[400px]",
            "xl:max-2xl:max-h-[300px]"
          )}
          role="region"
          aria-label="Store search results"
          aria-live="polite"
        >
          {storeCount === 0 ? (
            <div className="text-base-content/70 p-4 text-center text-xs">
              No stores found in this area
            </div>
          ) : (
            <ul className="menu menu-sm w-full gap-0.5 p-0" role="list">
              {stores.map((store) => {
                const isSelected = selectedStoreWotcId === store.wotcId;
                return (
                  <li key={store.wotcId} className="w-full" role="listitem">
                    <button
                      data-store-id={store.wotcId}
                      onClick={() => onStoreClick(store)}
                      className={cn(
                        "btn btn-soft flex w-full items-center justify-start gap-2 p-2 text-left transition-colors",
                        isSelected && "bg-primary/20 hover:bg-primary/30"
                      )}
                      title={store.name}
                      aria-label={`${store.name}${isSelected ? " (selected)" : ""}`}
                      aria-current={isSelected ? "true" : undefined}
                    >
                      <LgsLogo
                        wotcId={store.wotcId}
                        storeName={store.name}
                        isTradingHub={store.isTradingHub}
                        size="xs"
                        className="shrink-0"
                        fallbackClassName={
                          isSelected ? "text-primary" : "text-base-content/60"
                        }
                      />
                      <span className="text-base-content block min-w-0 flex-1 truncate text-xs">
                        {store.name}
                      </span>
                      {store.userCount !== undefined && store.userCount > 0 && (
                        <div className="text-base-content/60 flex shrink-0 items-center gap-1 text-xs">
                          <User size={12} />
                          <span>{store.userCount}</span>
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
